import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useShallow } from 'zustand/react/shallow';
import { IEventChecklist } from '@clickersphere/core/entities/IEventChecklist';
import { ResponseError } from '@clickersphere/core/entities/ResponseError';
import { useCreateEventChecklist } from '../../../../app/hooks/event-checklist/useCreateEventChecklist';
import { useUpdateEventChecklist } from '../../../../app/hooks/event-checklist/useUpdateEventChecklist';
import { useError } from '../../../../app/hooks/useError';
import useToast from '../../../../app/hooks/useToast';
import { useStore } from '../../../../app/store';

export function useEventChecklistModalController(
  eventChecklist?: IEventChecklist,
) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { handleError } = useError();

  const { handleCloseModal } = useStore(
    useShallow((state) => ({
      handleCloseModal: state.modal.handleCloseModal,
    })),
  );

  const { createEventChecklistAsync, createEventChecklistIsPending } =
    useCreateEventChecklist();
  const { updateEventChecklistAsync, updateEventChecklistIsPending } =
    useUpdateEventChecklist();

  const schema = useMemo(
    () =>
      z.object({
        name: z
          .string()
          .trim()
          .nonempty(t('settings.checklists.modal.errors.name.required'))
          .max(120, t('settings.checklists.modal.errors.name.maxLength')),
        items: z
          .array(
            z.object({
              title: z
                .string()
                .trim()
                .nonempty(
                  t('settings.checklists.modal.errors.itemTitle.required'),
                ),
              offsetDays: z.number().int(),
            }),
          )
          .min(1, t('settings.checklists.modal.errors.items.required')),
      }),
    [t],
  );

  type FormData = z.infer<typeof schema>;

  const {
    register,
    control,
    handleSubmit: hookFormHandleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: eventChecklist?.name ?? '',
      items:
        eventChecklist?.items.map((item) => ({
          title: item.title,
          offsetDays: item.offsetDays,
        })) ?? [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items',
  });

  function handleAddItem() {
    append({ title: '', offsetDays: 0 });
  }

  const handleSubmit = hookFormHandleSubmit(async (data) => {
    const payload = {
      name: data.name,
      items: data.items.map((item, index) => ({
        title: item.title,
        offsetDays: item.offsetDays,
        displayOrder: index,
      })),
    };

    try {
      if (eventChecklist) {
        await updateEventChecklistAsync({
          id: eventChecklist.id,
          data: payload,
        });
        toast(t('settings.checklists.modal.toast.updated'), 'success');
      } else {
        await createEventChecklistAsync(payload);
        toast(t('settings.checklists.modal.toast.created'), 'success');
      }
      handleCloseModal();
    } catch (error) {
      if (error instanceof ResponseError) {
        handleError(error);
        return;
      }
      toast(t('settings.checklists.modal.toast.error'), 'error');
    }
  });

  return {
    t,
    register,
    errors,
    fields,
    handleAddItem,
    handleRemoveItem: remove,
    handleSubmit,
    handleClose: handleCloseModal,
    isPending: createEventChecklistIsPending || updateEventChecklistIsPending,
  };
}
